import "../compliance/compliance.css";
import "./uniform.css";
import { useEffect, useMemo, useRef, useState } from "react";
import { DatePicker, Drawer, Input, InputNumber, Modal, Radio, Select, Tag } from "antd";
import SignatureCanvas from "react-signature-canvas";
import TextArea from "antd/es/input/TextArea";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { routes } from "@src/util/routes/routes";
import dayjs from "dayjs";
import debounce from "lodash/debounce";
import Spinner from "@src/components/spinner";
import { setNotification } from "@src/middleware/redux/reducers/notification.reducer";
import { GetEmployeeRecord } from "@src/util/query/employee.query";
import { getUniformItemsQuery, getUniformStandardsQuery } from "@src/util/query/uniform.query";
import {
    approveUniformRequestMutation,
    cancelUniformRequestMutation,
    issueUniformRequestMutation,
    logUniformIssueMutation,
    rejectUniformRequestMutation,
} from "@src/util/mutation/uniform.mutation";
import { CaretDownFilled } from "@ant-design/icons";

export const REASONS = [
    { value: "replacement", label: "Replacement" },
    { value: "damaged", label: "Damaged" },
    { value: "lost", label: "Lost" },
    { value: "newStarter", label: "New starter" },
    { value: "other", label: "Other" },
];
export const reasonLabel = (value) => REASONS.find((r) => r.value === value)?.label || value || "";
/** Request statuses as shown everywhere (stored as pending / approved / rejected / issued / cancelled) */
export const STATUS = {
    pending: { label: "Pending Approval", color: "gold" },
    approved: { label: "Approved", color: "blue" },
    rejected: { label: "Declined", color: "red" },
    issued: { label: "Issued", color: "green" },
    cancelled: { label: "Cancelled", color: "default" },
};
export const StatusTag = ({ status }) => <Tag color={STATUS[status]?.color}>{STATUS[status]?.label || status}</Tag>;
export const SOURCE_LABEL = { mobile: "Mobile App", web: "Web Portal" };
export const ukDate = (value) => (value ? dayjs(value).format("DD/MM/YYYY") : "--");
export const lineText = (line) => `${line.quantity} x ${line.itemName}${line.size ? ` (${line.size})` : ""}`;

/** One line per item asked for / given, e.g. "2 x Jacket (L)" */
export const Lines = ({ lines }) => (
    <div className="uni-lines">{(lines || []).map((line, i) => <span key={i}>{lineText(line)}</span>)}</div>
);

// the module's standards in a line, wherever it says what it's built to
export const MODULE_STANDARD = "BS 7499:2020 / BS 10800:2020 §7.6";

/**
 * The standards the module is built to (wfc-server uniformStandards): the standards, the item
 * categories and the ratings each can carry, the standard size lists, the catalogue and the
 * return outcomes. The same for every company, so read once.
 */
export const useUniformStandards = (skip = false) => {
    const { data, loading } = useQuery(getUniformStandardsQuery, { skip, fetchPolicy: "cache-first" });
    const value = data?.getUniformStandards?.response;
    return useMemo(() => {
        const standards = value?.standards || [];
        const categories = value?.categories || [];
        const sizeLists = value?.sizeLists || [];
        return {
            loading, standards, categories, sizeLists,
            catalogue: value?.catalogue || [],
            returnOutcomes: value?.returnOutcomes || [],
            categoryOf: (key) => categories.find((c) => c.key === key) || categories.find((c) => c.key === "other") || { key: "other", label: "Other", ratings: [] },
            standardOf: (key) => standards.find((st) => st.key === key),
            sizeListOf: (key) => sizeLists.find((list) => list.key === key),
        };
    }, [value, loading]);
};

/** The standard an item is bought to, e.g. "EN ISO 20471 Class 2" */
export const RatingTag = ({ rating }) => (rating ? <Tag className="uni-rating">{rating}</Tag> : null);

/** What each issued-log row still holds: "Held", "1 of 2 held", "Returned" */
export const heldText = (row) => {
    const held = Number.isInteger(row?.held) ? row.held : row?.quantity || 0;
    if (held === row?.quantity) return "Held";
    if (held === 0) return (row.returns || []).every((r) => r.outcome === "notRecovered") ? "Not recovered" : "Returned";
    return `${held} of ${row.quantity} held`;
};

/** Downloads rows as a CSV file */
export const downloadCsv = (fileName, header, rows) => {
    const cell = (value) => {
        const text = value === null || value === undefined ? "" : String(value);
        return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };
    const csv = [header, ...rows].map((row) => row.map(cell).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
};

const ModalShell = ({ visible, close, title, children, footer, width = 620 }) => (
    <Modal open={visible} width={width} footer={footer} closable={false} onCancel={close} zIndex={1100} destroyOnClose>
        <div className="modal-header-title">
            <span style={{ letterSpacing: "0.2px" }}>{title}</span>
            <span onClick={close}><FontAwesomeIcon className="close" icon={faClose} /></span>
        </div>
        <div className="modal-body" style={{ margin: "16px 0 0" }}>{children}</div>
    </Modal>
);

/**
 * The right-hand drawer the profile's other forms use (Add Uniform Item, Upload file), above
 * the dashboard's full-screen pop-up when opened from it.
 */
export const DrawerShell = ({ visible, close, title, children, footer, width = 640 }) => (
    <Drawer
        title={title}
        placement="right"
        closable={true}
        onClose={close}
        closeIcon={<FontAwesomeIcon icon={faClose} onClick={close} className='close-icon' />}
        open={visible}
        width={width}
        maskClosable={false}
        mask={false}
        zIndex={1100}
        destroyOnClose
        footer={footer}
    >
        {children}
    </Drawer>
);

const Footer = ({ busy, disabled, label, close, hint }) => (
    <div className="drawer-footer" style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button className={busy || disabled ? "disabled-btn drawer-filled-btn" : "drawer-filled-btn"} disabled={busy || disabled} onClick={label.onClick}>
            {busy ? <Spinner /> : label.text}
        </button>
        <button className={busy ? "disabled-btn drawer-outlined-btn" : "drawer-outlined-btn"} disabled={busy} onClick={close}>Cancel</button>
        {hint && <span className="cmp-hint">{hint}</span>}
    </div>
);

const useNotify = () => {
    const dispatch = useDispatch();
    return (message, error = false) => dispatch(setNotification({ error, notificationState: true, message }));
};

let lineKey = 0;
const newLine = (line = {}) => ({ key: ++lineKey, itemId: line.itemId ? String(line.itemId) : null, size: line.size || null, quantity: line.quantity || 1 });
/** What's still to choose before the lines can be sent */
const linesMissing = (lines, items) => {
    if (!lines.length) return "an item";
    for (const line of lines) {
        const item = items.find((i) => i._id === line.itemId);
        if (!item) return "an item on every line";
        if (item.sizes?.length && !line.size) return `a size for ${item.name}`;
    }
    return null;
};
const linesInput = (lines) => lines.map((line) => ({ itemId: line.itemId, size: line.size || null, quantity: line.quantity }));

/** Items to choose from, grouped by category in the standard order, with the rating beside the name */
const itemOptions = (items, categories) => {
    const order = categories.map((c) => c.key);
    const groups = new Map();
    for (const item of items) {
        const key = order.includes(item.category) ? item.category : "other";
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push({ value: item._id, label: item.rating ? `${item.name} (${item.rating})` : item.name });
    }
    if (!categories.length) return items.map((i) => ({ value: i._id, label: i.name }));
    return [...groups.entries()]
        .sort(([a], [b]) => order.indexOf(a) - order.indexOf(b))
        .map(([key, options]) => ({ label: categories.find((c) => c.key === key)?.label || "Other", options }));
};

/**
 * Item / size / quantity rows. Sizes come from the chosen item; an item without sizes is
 * one size.
 */
export const LinesEditor = ({ items, lines, setLines }) => {
    const { categories } = useUniformStandards();
    const change = (key, patch) => setLines(lines.map((line) => (line.key === key ? { ...line, ...patch } : line)));
    return (
        <div className="uni-editor">
            <table className="uni-editor-table">
                <colgroup>
                    <col /><col className="uni-col-size" /><col className="uni-col-qty" /><col className="uni-col-remove" />
                </colgroup>
                <thead>
                    <tr><th>Item</th><th>Size</th><th>Quantity</th><th /></tr>
                </thead>
                <tbody>
                    {lines.map((line) => {
                        const item = items.find((i) => i._id === line.itemId);
                        return (
                            <tr key={line.key}>
                                <td>
                                    <Select className="custom-select" suffixIcon={<CaretDownFilled style={{marginTop:'-16px'}}/>} showSearch optionFilterProp="label" placeholder="Choose an item" value={line.itemId} dropdownStyle={{ minWidth: 340 }}
                                        onChange={(value) => change(line.key, { itemId: value, size: null })}
                                        options={itemOptions(items, categories)} />
                                </td>
                                <td>
                                    {item && !item.sizes?.length
                                        ? <span className="cmp-muted uni-onesize">One size</span>
                                        : <Select className="custom-select"  suffixIcon={<CaretDownFilled style={{marginTop:'-16px'}}/>}  placeholder="Size" value={line.size} disabled={!item}
                                            onChange={(value) => change(line.key, { size: value })}
                                            options={(item?.sizes || []).map((s) => ({ value: s, label: s }))} />}
                                </td>
                                <td style={{paddingTop:'10px', display:'flex', justifyContent:'center', gap:8}}>
                                    <InputNumber style={{height:'35px'}}  min={1} max={5} precision={0} value={line.quantity} onChange={(value) => change(line.key, { quantity: value || 1 })} />
                                {
                                    lines?.length>1 ? 
                                    
                                        <button className="uni-remove" title="Remove" disabled={lines.length === 1} onClick={() => setLines(lines.filter((l) => l.key !== line.key))}>
                                            <FontAwesomeIcon icon={faTrashCan} />
                                        </button>
                                    
                                    
                                    : null
                                }
                                </td>

                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {lines.length < 20 && <button className="cmp-link" onClick={() => setLines([...lines, newLine()])}>+ Add another item</button>}
        </div>
    );
};

const useActiveItems = (visible) => {
    const { data, loading } = useQuery(getUniformItemsQuery, { skip: !visible, fetchPolicy: "network-only" });
    return { items: data?.getUniformItems?.response || [], loading };
};

const NoItems = () => <div className="cmp-hint" style={{ marginBottom: 12 }}>No uniform items are set up yet. Add them in Settings &gt; Uniform.</div>;

// ---------------------------------------------------------------- signing for uniform

/**
 * A pad to sign on with a mouse, finger or stylus; reports a PNG data URL, or null when
 * cleared. Sized to its box so strokes land where they're drawn.
 */
export const SignaturePad = ({ onChange, height = 150 }) => {
    const box = useRef(null);
    const pad = useRef(null);
    const [width, setWidth] = useState(0);
    const [drawn, setDrawn] = useState(false);
    useEffect(() => { if (box.current) setWidth(Math.floor(box.current.clientWidth)); }, []);
    const report = () => {
        const isDrawn = !!pad.current && !pad.current.isEmpty();
        setDrawn(isDrawn);
        if (!isDrawn) return onChange(null);
        let canvas;
        try { canvas = pad.current.getTrimmedCanvas(); } catch { canvas = pad.current.getCanvas(); }
        onChange(canvas.toDataURL("image/png"));
    };
    return (
        <div>
            <div className="uni-sign-pad" ref={box}>
                {width > 0 && <SignatureCanvas ref={pad} penColor="#111827" canvasProps={{ width, height, className: "uni-sign-canvas" }} onEnd={report} />}
            </div>
            <div className="uni-sign-bar">
                <span className="cmp-hint">{drawn ? "Signed" : "Sign in the box"}</span>
                <button type="button" className="cmp-link" onClick={() => { pad.current?.clear(); report(); }}>Clear</button>
            </div>
        </div>
    );
};

/** What the officer agrees to - the server words the receipt itself, with the company's name */
export const DECLARATION_PREVIEW = "I confirm I have received the items listed. They remain company property: I will wear them only while on duty, look after them, and return them when asked or when my employment ends. Items marked personal issue are mine to keep.";

/**
 * How the officer signs for what's handed over (BS 7499: officers sign for uniform and
 * undertake to return it): in the app afterwards, or on this screen now.
 */
const SignFor = ({ name, mode, setMode, setSignature }) => (
    <section className="uni-form-section">
        <div className="uni-form-title">Officer's signature</div>
        <div className="uni-form-note">They sign for what they receive and undertake to return company property when they leave.</div>
        <Radio.Group value={mode} onChange={(e) => { setMode(e.target.value); setSignature(null); }} className="uni-signfor-modes">
            <Radio value="app">Send to {name || "the officer"}'s app to sign</Radio>
            <Radio value="here">{name || "The officer"} signs here now</Radio>
        </Radio.Group>
        {mode === "here" && (
            <div className="uni-declaration-box">
                <div className="uni-declaration">{DECLARATION_PREVIEW}</div>
                <SignaturePad onChange={setSignature} />
            </div>
        )}
    </section>
);

/** Items heading, with the way to the company's list (Settings > Uniform) for those who manage it */
const ItemsTitle = ({ onLeave }) => {
    const navigate = useNavigate();
    const { authenticatedUserDetail } = useSelector((state) => state.userAuthReducer);
    const canManage = authenticatedUserDetail?.permission?.Uniform?.access !== false;
    return (
        <div className="uni-form-title uni-form-title-row">
            <span>Items</span>
            {canManage && <button className="cmp-link" onClick={() => { onLeave?.(); navigate(routes.uniformSettings); }}>Manage uniform list</button>}
        </div>
    );
};

/** Date issued and an optional note, side by side */
const WhenAndNote = ({ issuedDate, setIssuedDate, note, setNote }) => (
    <section className="uni-form-section">
        <div className="uni-form-title">When</div>
        <div className="uni-form-row">
            <div>
                <label className="uni-form-label">Date issued</label>
                <DatePicker className="generic-input-control" style={{ width: "100%" }} format="DD/MM/YYYY" value={issuedDate} allowClear={false}
                    disabledDate={(d) => d && d.isAfter(dayjs(), "day")} onChange={(value) => value && setIssuedDate(value)} />
            </div>
            <div>
                <label className="uni-form-label">Note <span className="cmp-muted">(optional)</span></label>
                <Input className="generic-input-control" value={note} maxLength={500} placeholder="e.g. Starter kit" onChange={(e) => setNote(e.target.value)} />
            </div>
        </div>
    </section>
);

// ---------------------------------------------------------------- uniform handed over without a request

export const LogIssueDrawer = ({ visible, employee = null, onClose, onSaved }) => {
    const notify = useNotify();
    const { items, loading: itemsLoading } = useActiveItems(visible);
    const [employeeId, setEmployeeId] = useState(null);
    const [employeeOptions, setEmployeeOptions] = useState([]);
    const [lines, setLines] = useState([newLine()]);
    const [issuedDate, setIssuedDate] = useState(dayjs());
    const [note, setNote] = useState("");
    const [signMode, setSignMode] = useState("app");
    const [signature, setSignature] = useState(null);
    const [searchEmployees, { data: employeeData, loading: searching }] = useLazyQuery(GetEmployeeRecord);
    const [log, { loading }] = useMutation(logUniformIssueMutation);

    useEffect(() => {
        if (!visible) return;
        setEmployeeId(employee?._id || null);
        setEmployeeOptions(employee ? [{ value: employee._id, label: employee.name }] : []);
        setLines([newLine()]);
        setIssuedDate(dayjs());
        setNote("");
        setSignMode("app");
        setSignature(null);
    }, [visible, employee?._id]);

    useEffect(() => {
        const rows = employeeData?.getEmployee?.response || [];
        if (rows.length) setEmployeeOptions(rows.map((e) => ({ value: e._id, label: `${e.firstname || ""} ${e.lastname || ""}`.replace(/\s+/g, " ").trim() })));
    }, [employeeData]);

    const search = useMemo(() => debounce((value) => {
        if (value?.trim().length >= 2) searchEmployees({ variables: { input: { filters: null, search: value.trim(), page: 1, pageSize: 20 } }, fetchPolicy: "network-only" });
    }, 350), []);

    const missing = !employeeId ? "the employee" : linesMissing(lines, items) || (signMode === "here" && !signature ? "SIGN" : null);
    const employeeName = employeeOptions.find((o) => o.value === employeeId)?.label;
    const submit = async () => {
        try {
            await log({ variables: { input: { employeeId, lines: linesInput(lines), issuedDate: issuedDate.format("YYYY-MM-DD"), note: note.trim() || null, signature: signMode === "here" ? signature : null } } });
            notify(signMode === "here" ? "Uniform issued and signed for" : "Uniform issued - the officer has been asked to sign for it");
            onSaved?.();
            onClose();
        } catch (err) {
            notify(err.message, true);
        }
    };

    return (
        <DrawerShell visible={visible} close={onClose} title="Issue Uniform"
            footer={<Footer busy={loading} disabled={!!missing} close={onClose} hint={missing && (missing === "SIGN" ? "The officer needs to sign" : `Choose ${missing}`)} label={{ text: "Issue uniform", onClick: submit }} />}>
            <div className="uni-form">
                <section className="uni-form-section">
                    <div className="uni-form-title">Officer</div>
                    <Select className="custom-select" showSearch suffixIcon={<CaretDownFilled />} filterOption={false} disabled={!!employee} value={employeeId} placeholder="Type a name to search"
                        notFoundContent={searching ? <Spinner /> : null} onSearch={search} onChange={setEmployeeId} options={employeeOptions} />
                </section>
                <section className="uni-form-section">
                    <ItemsTitle onLeave={onClose} />
                    {itemsLoading ? <Spinner /> : items.length ? <LinesEditor items={items} lines={lines} setLines={setLines} /> : <NoItems />}
                </section>
                <WhenAndNote issuedDate={issuedDate} setIssuedDate={setIssuedDate} note={note} setNote={setNote} />
                {employeeId && <SignFor name={employeeName} mode={signMode} setMode={setSignMode} setSignature={setSignature} />}
            </div>
        </DrawerShell>
    );
};

// ---------------------------------------------------------------- deciding a request

const RequestSummary = ({ request }) => (
    <div className="uni-summary">
        <div className="cmp-person-name">{request?.employeeName}</div>
        <div className="cmp-muted">{reasonLabel(request?.reason)} · asked {ukDate(request?.createdAt)}</div>
        <Lines lines={request?.lines} />
        {request?.note && <div className="uni-quote">"{request.note}"</div>}
    </div>
);

const DECISIONS = {
    approve: { title: "Approve uniform request", button: "Approve", done: "Uniform request approved", noteLabel: "Note for the officer", mutation: approveUniformRequestMutation },
    reject: { title: "Decline uniform request", button: "Decline", done: "Uniform request declined", noteLabel: "Reason", mutation: rejectUniformRequestMutation },
    cancel: { title: "Cancel uniform request", button: "Cancel request", done: "Uniform request cancelled", noteLabel: "Reason", mutation: cancelUniformRequestMutation },
};

/** Approve / decline / cancel, with an optional note the officer sees */
export const UniformDecisionModal = ({ mode, request, onClose, onDone }) => {
    const notify = useNotify();
    const decision = DECISIONS[mode] || DECISIONS.approve;
    const [note, setNote] = useState("");
    const [run, { loading }] = useMutation(decision.mutation);
    useEffect(() => { setNote(""); }, [mode, request?._id]);

    const submit = async () => {
        try {
            await run({ variables: { id: request._id, note: note.trim() || null } });
            notify(decision.done);
            onDone?.();
            onClose();
        } catch (err) {
            notify(err.message, true);
        }
    };

    return (
        <ModalShell visible={!!mode && !!request} close={onClose} title={decision.title} width={540}
            footer={<Footer busy={loading} close={onClose} label={{ text: decision.button, onClick: submit }} />}>
            <RequestSummary request={request} />
            <div className="cmp-field">
                <label>{decision.noteLabel} <span className="cmp-muted">(optional)</span></label>
                <TextArea value={note} maxLength={500} rows={3} onChange={(e) => setNote(e.target.value)} />
            </div>
        </ModalShell>
    );
};

/**
 * Mark a request issued. Starts from what was asked for; change a size or quantity if
 * something else was handed over - the issued log records what was actually given.
 */
export const IssueRequestDrawer = ({ request, onClose, onDone }) => {
    const notify = useNotify();
    const { items, loading: itemsLoading } = useActiveItems(!!request);
    const [lines, setLines] = useState([]);
    const [issuedDate, setIssuedDate] = useState(dayjs());
    const [note, setNote] = useState("");
    const [signMode, setSignMode] = useState("app");
    const [signature, setSignature] = useState(null);
    const [issue, { loading }] = useMutation(issueUniformRequestMutation);
    useEffect(() => {
        if (!request) return;
        setLines((request.lines || []).map(newLine));
        setIssuedDate(dayjs());
        setNote("");
        setSignMode("app");
        setSignature(null);
    }, [request?._id]);

    const gone = lines.filter((line) => !items.some((i) => i._id === line.itemId));
    const missing = itemsLoading ? "" : linesMissing(lines, items) || (signMode === "here" && !signature ? "SIGN" : null);
    const submit = async () => {
        try {
            await issue({ variables: { id: request._id, lines: linesInput(lines), issuedDate: issuedDate.format("YYYY-MM-DD"), note: note.trim() || null, signature: signMode === "here" ? signature : null } });
            notify(signMode === "here" ? "Uniform issued and signed for" : "Uniform issued - the officer has been asked to sign for it");
            onDone?.();
            onClose();
        } catch (err) {
            notify(err.message, true);
        }
    };

    return (
        <DrawerShell visible={!!request} close={onClose} title="Issue Uniform"
            footer={<Footer busy={loading} disabled={!!missing || itemsLoading} close={onClose} hint={missing && (missing === "SIGN" ? "The officer needs to sign" : `Choose ${missing}`)} label={{ text: "Mark as issued", onClick: submit }} />}>
            <div className="uni-form">
                <section className="uni-form-section">
                    <div className="uni-form-title">Request</div>
                    <RequestSummary request={request} />
                    {request?.status === "pending" && <div className="uni-form-note">This request hasn't been approved yet - issuing it approves it too.</div>}
                </section>
                <section className="uni-form-section">
                    <ItemsTitle onLeave={onClose} />
                    <div className="uni-form-note">Starts from what they asked for - change a size or quantity if something else is handed over.</div>
                    {itemsLoading ? <Spinner /> : <LinesEditor items={items} lines={lines} setLines={setLines} />}
                    {gone.length > 0 && !itemsLoading && <div className="uni-form-note">An item on this request has been archived - choose what was given instead.</div>}
                </section>
                <WhenAndNote issuedDate={issuedDate} setIssuedDate={setIssuedDate} note={note} setNote={setNote} />
                <SignFor name={request?.employeeName} mode={signMode} setMode={setSignMode} setSignature={setSignature} />
            </div>
        </DrawerShell>
    );
};
